import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import {
  Plane, BedDouble, ShieldCheck, FileText, MapPin, CheckCircle2, Clock,
  ArrowRight, Stamp, Users, Sparkles, Star, Quote, ClipboardList, Car,
  FileSignature, Plus,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import api from "@/lib/api";
import HeroApplicationCard from "@/components/HeroApplicationCard";
import { ALL_MOCKUPS, PreviewCard } from "@/components/DocumentMockups";
import { labelForItems } from "@/lib/addons";
import ScrollRow from "@/components/ScrollRow";
import { SEO } from "@/components/SEO";
import { FAQS } from "@/lib/faqData";
import { SITE_URL, SITE_NAME, OG_IMAGE } from "@/lib/seo";

const PACKAGE_ICONS = { basic: ShieldCheck, complete: Sparkles, premium: Users };

const TESTIMONIALS = [
  { name: "Ahmed K.", country: "Saudi Arabia", text: "Got my Italy visa approved. The flight reservation PNR was verifiable at the embassy. Saved me $400 vs booking real tickets." },
  { name: "Yusuf D.", country: "Turkey", text: "Complete package arrived in 45 minutes. Hotel booking was a real reservation on booking.com. Embassy didn't even ask questions." },
  { name: "Fatima A.", country: "UAE", text: "Family package for 4 was a steal. Everything organized in one folder. Schengen file made the appointment so smooth." },
  { name: "Omar S.", country: "Saudi Arabia", text: "Tried other services, this was the only one with a verifiable PNR. Worth every dollar for peace of mind at the appointment." },
  { name: "Selin Y.", country: "Turkey", text: "Spain visa approved. The cover letter and itinerary were professionally written. I'd recommend the Complete package." },
  { name: "Khalid M.", country: "UAE", text: "Insurance certificate from a recognized provider. Got my France visa in 10 days. Excellent service." },
];

const FAQS_LOCAL = null; // FAQ content now lives in `@/lib/faqData` for reuse in SEO JSON-LD

const ORGANIZATION_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE_NAME,
  legalName: "Trip360 Travel & Tourism Co.",
  url: SITE_URL,
  logo: `${SITE_URL}/og-image.png`,
  image: OG_IMAGE,
  description: "Schengen visa document preparation service. Verifiable flight reservations, real hotel bookings, insurance and application forms delivered in 24 hours.",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Riyadh",
    addressCountry: "SA",
  },
  contactPoint: [{
    "@type": "ContactPoint",
    contactType: "customer support",
    email: "support@schengenfile.com",
    availableLanguage: ["en", "ar", "tr"],
  }],
};

function buildFaqJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export default function Landing() {
  const [packages, setPackages] = useState([]);

  useEffect(() => {
    api.get("/packages").then((r) => setPackages(Array.isArray(r.data) ? r.data : [])).catch(() => setPackages([]));
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="Schengen Visa Documents Ready in 24 Hours | SchengenFile"
        description="Complete Schengen visa document packages — verifiable flight PNR, real hotel bookings, €30,000 travel insurance and application form. Delivered in 24 hours or 3 hours express."
        jsonLd={[ORGANIZATION_JSONLD, buildFaqJsonLd()]}
      />
      <Header />

      <main>
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative bg-[#0f1b2d] overflow-hidden"
      >
        <div className="absolute inset-0 hero-grain opacity-30" />
        <div
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage: "url('https://images.pexels.com/photos/19671961/pexels-photo-19671961.jpeg')",
            backgroundSize: "cover",
            backgroundPosition: "center right",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0f1b2d] via-[#0f1b2d]/95 to-[#0f1b2d]/60" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 text-white reveal">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/15 text-xs font-medium tracking-wider uppercase text-white/80 mb-6">
              <Stamp className="w-3.5 h-3.5 text-[#60a5fa]" /> Trusted by applicants in 189+ countries
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-black leading-[1.05] tracking-tight">
              Your Appointment Is Booked. <span className="text-[#60a5fa]">Now Get Your Visa File Ready in 24 Hours.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-white/75 max-w-xl leading-relaxed">
              You already have your appointment. But gathering the right documents, in the right format, accepted by the consulate, is where most applicants make costly mistakes. We handle everything for you, so you walk in confident and prepared.
            </p>

            <ul className="mt-6 max-w-xl space-y-2.5 hidden md:block" data-testid="hero-bullets-list">
              {[
                { icon: ClipboardList, title: "Visa Application Form", desc: "filled and formatted correctly" },
                { icon: Plane,         title: "Flight Reservation with valid PNR", desc: "verifiable by the consulate" },
                { icon: BedDouble,     title: "Hotel Booking Confirmation", desc: "for every night of your stay" },
                { icon: Car,           title: "Car Rental Reservation", desc: "if required by your itinerary" },
                { icon: FileSignature, title: "Cover Letter", desc: "professionally written for your consulate" },
                { icon: MapPin,        title: "Travel Itinerary & Day-by-Day Plan", desc: "shows your full trip" },
                { icon: ShieldCheck,   title: "Travel Insurance (€30,000)", desc: "Schengen-recognized provider" },
                { icon: Plus,          title: "And more", desc: "NOC, Sponsor Letter, Invitation Letter & add-ons" },
              ].map((b, i) => {
                const Icon = b.icon;
                return (
                  <li key={i} className="flex items-start gap-3 text-sm leading-snug" data-testid={`hero-bullet-${i}`}>
                    <span className="flex-shrink-0 w-7 h-7 rounded-md bg-white/10 border border-white/15 grid place-items-center">
                      <Icon className="w-3.5 h-3.5 text-[#60a5fa]" />
                    </span>
                    <span className="pt-0.5">
                      <span className="font-semibold text-white">{b.title}</span>
                      <span className="text-white/65"> — {b.desc}</span>
                    </span>
                  </li>
                );
              })}
            </ul>

            {/* Mobile hero card — between bullets and CTAs */}
            <div className="mt-8 lg:hidden">
              <HeroApplicationCard defaultPackages={packages} />
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/apply"
                data-testid="hero-cta-apply"
                className="inline-flex items-center gap-2 bg-[#2563eb] hover:bg-[#1d4ed8] text-white font-semibold px-6 py-3 rounded-md transition-all shadow-lg shadow-blue-900/30"
              >
                Apply Now <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="#how"
                data-testid="hero-secondary"
                className="inline-flex items-center gap-2 bg-transparent hover:bg-white/10 border border-white/20 text-white font-semibold px-6 py-3 rounded-md transition-all"
              >
                How it works
              </a>
            </div>
            <div className="mt-4 max-w-md">
              <Link
                to="/build-your-file"
                data-testid="hero-build-your-file-link"
                className="block rounded-md border border-[#2563eb] bg-[#eff6ff] hover:bg-[#dbeafe] transition-colors px-4 py-3"
              >
                <div className="text-sm font-bold text-[#0f1b2d]">Need only specific documents?</div>
                <div className="text-[13px] text-[#2563eb]">Build your own file and pay per document →</div>
              </Link>
            </div>
            <div className="mt-10 grid grid-cols-3 max-w-md gap-6">
              <Stat n="98%" label="On-time delivery" />
              <Stat n="14k+" label="Files prepared" />
              <Stat n="4.9/5" label="Customer rating" />
            </div>
          </div>

          <div className="hidden lg:flex lg:col-span-5 justify-end">
            <HeroApplicationCard defaultPackages={packages} />
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" data-testid="how-it-works-section" className="py-20 md:py-28 bg-[#f8fafc]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#2563eb]">3 simple steps</div>
            <h2 className="mt-2 text-3xl sm:text-4xl font-display font-bold text-[#0f1b2d]">From appointment to ready file</h2>
            <p className="mt-4 text-[#475569]">No travel agency. No queue. No risk of a real ticket you don't need.</p>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { n: "01", t: "Fill in your details", d: "Tell us your destination, dates and number of applicants. The form auto-saves as you go.", art: <FormArt /> },
              { n: "02", t: "Pay securely", d: "Pay with card via Stripe — encrypted and PCI compliant. Refund guarantee if not delivered on time.", art: <PayArt /> },
              { n: "03", t: "Receive your file", d: "Download every document from your dashboard within 24 hours. Bring it to your appointment.", art: <FileArt /> },
            ].map((s) => (
              <div key={s.n} data-testid={`step-${s.n}`} className="p-8 bg-white border border-[#e2e8f0] rounded-xl shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center gap-4">
                  <div className="font-display font-black text-5xl text-[#2563eb] leading-none">{s.n}</div>
                  <h3 className="font-display font-bold text-xl text-[#0f1b2d]">{s.t}</h3>
                </div>
                <p className="mt-3 text-sm text-[#475569] leading-relaxed">{s.d}</p>
                <div className="mt-6 rounded-lg bg-[#f8fafc] border border-[#e2e8f0] p-4 h-48 grid place-items-center overflow-hidden">
                  {s.art}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT YOU GET — package-tabbed preview */}
      <WhatYouGet packages={packages} />


      {/* PRICING */}
      <section id="pricing" data-testid="pricing-section" className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#2563eb]">Pricing</div>
            <h2 className="mt-2 text-3xl sm:text-4xl font-display font-bold text-[#0f1b2d]">Pick the package that fits your trip</h2>
            <p className="mt-4 text-[#475569]">All prices in USD. One-time payment, no subscription.</p>
          </div>
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
            {packages.map((p) => {
              const Icon = PACKAGE_ICONS[p.id] || ShieldCheck;
              const popular = p.most_popular || p.id === "complete";
              return (
                <div
                  key={p.id}
                  data-testid={`pricing-card-${p.id}`}
                  className={`relative bg-white rounded-lg p-8 flex flex-col ${popular ? "popular-ring lg:scale-[1.03]" : "border border-[#e2e8f0] hover:shadow-md"} transition-all`}
                >
                  {popular && (
                    <div className="absolute -top-3 left-8 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest bg-[#2563eb] text-white rounded">Most popular</div>
                  )}
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-md bg-[#eff6ff] grid place-items-center text-[#2563eb]">
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="font-display font-bold text-xl text-[#0f1b2d]">{p.name}</div>
                  </div>
                  <div className="mt-6 flex items-baseline gap-1">
                    <span className="text-5xl font-display font-black text-[#0f1b2d]">${Math.round(p.price_normal ?? 0)}</span>
                    <span className="text-sm text-[#64748b]">/ file</span>
                  </div>
                  <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#475569]">
                    <Clock className="w-3.5 h-3.5" /> Delivery in 24 hours
                  </div>
                  <div className="mt-2 text-xs text-[#475569]">{labelForItems(p.items || [])}</div>
                  <div className="mt-5 text-xs text-[#64748b] border-t border-[#e2e8f0] pt-4">
                    <span className="inline-flex items-center gap-1.5"><span>⚡</span> Express available: <span className="font-semibold text-[#0f1b2d]">${Math.round(p.price_express ?? 0)}</span></span>
                  </div>
                  <Link
                    to={`/apply?package=${p.id}`}
                    data-testid={`select-package-${p.id}`}
                    className={`mt-6 inline-flex items-center justify-center gap-2 font-semibold py-3 px-5 rounded-md transition-all ${popular ? "bg-[#2563eb] text-white hover:bg-[#1d4ed8]" : "bg-[#0f1b2d] text-white hover:bg-[#1e293b]"}`}
                  >
                    Get {p.name} <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section data-testid="testimonials-section" className="py-20 md:py-28 bg-[#0f1b2d] text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#60a5fa]">Reviews</div>
              <h2 className="mt-2 text-3xl sm:text-4xl font-display font-bold">Trusted by thousands of travelers</h2>
            </div>
            <div className="flex items-center gap-2 text-sm text-white/80">
              <div className="flex items-center text-[#fbbf24]">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" />)}
              </div>
              <span>4.9 average from 2,300+ reviews</span>
            </div>
          </div>
        </div>
        <div className="mt-12 relative">
          <div className="flex gap-6 animate-marquee w-max">
            {[...TESTIMONIALS, ...TESTIMONIALS].map((t, i) => (
              <div key={i} className="w-[360px] bg-white/5 border border-white/10 rounded-lg p-6 backdrop-blur-sm">
                <Quote className="w-5 h-5 text-[#60a5fa]" />
                <p className="mt-3 text-sm text-white/85 leading-relaxed">{t.text}</p>
                <div className="mt-4 text-xs text-white/60">{t.name} · {t.country}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" data-testid="faq-section" className="py-20 md:py-28">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#2563eb]">FAQ</div>
          <h2 className="mt-2 text-3xl sm:text-4xl font-display font-bold text-[#0f1b2d]">Frequently asked questions</h2>
          <div className="mt-10">
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-item-${i}`}>
                  <AccordionTrigger className="text-left text-[#0f1b2d] font-semibold">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-[#475569] leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#f8fafc] border-t border-[#e2e8f0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="font-display font-bold text-2xl text-[#0f1b2d]">Your appointment is coming up.</h3>
            <p className="text-[#475569]">Get your file ready in 3 hours.</p>
          </div>
          <Link
            to="/apply"
            data-testid="footer-cta-apply"
            className="inline-flex items-center gap-2 bg-[#2563eb] hover:bg-[#1d4ed8] text-white font-semibold px-6 py-3 rounded-md transition-all"
          >
            Apply now <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
      </main>

      <Footer />
    </div>
  );
}

function Stat({ n, label }) {
  return (
    <div>
      <div className="font-display font-black text-2xl text-white">{n}</div>
      <div className="text-xs text-white/60 uppercase tracking-wider">{label}</div>
    </div>
  );
}

function DocCard({ icon: Icon, title, desc, span = "" }) {
  return (
    <div className={`bg-white border border-[#e2e8f0] rounded-lg p-6 ${span}`}>
      <div className="w-10 h-10 rounded-md bg-[#eff6ff] grid place-items-center text-[#2563eb]">
        <Icon className="w-5 h-5" />
      </div>
      <h3 className="mt-4 font-display font-bold text-lg text-[#0f1b2d]">{title}</h3>
      <p className="mt-2 text-sm text-[#475569] leading-relaxed">{desc}</p>
    </div>
  );
}

function WhatYouGet({ packages }) {
  const order = ["basic", "complete", "premium"];
  const sorted = [...packages].sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id));
  const [active, setActive] = useState("complete");
  const pkg = sorted.find((p) => p.id === active) || sorted[0];
  if (!pkg) return null;
  const items = (pkg.items || []);
  return (
    <section id="documents" data-testid="what-you-get-section" className="py-20 md:py-28 bg-white border-y border-[#e2e8f0]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-2xl">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#2563eb]">What you get</div>
          <h2 className="mt-2 text-3xl sm:text-4xl font-display font-bold text-[#0f1b2d]">A complete, consulate-ready file</h2>
          <p className="mt-3 text-[#475569]">Switch between packages to see exactly which documents you'll receive.</p>
        </div>

        {/* Package tabs — always single row, even on mobile */}
        <div className="mt-8 flex flex-nowrap gap-1.5 sm:gap-2 w-full" data-testid="wyg-package-tabs">
          {sorted.map((p) => {
            const isActive = active === p.id;
            const price = p.price_normal;
            return (
              <button
                key={p.id}
                onClick={() => setActive(p.id)}
                data-testid={`wyg-tab-${p.id}`}
                className={`flex-1 min-w-0 px-2 sm:px-4 py-2 text-[11px] sm:text-sm font-semibold rounded-md border-2 transition truncate whitespace-nowrap ${isActive ? "border-[#2563eb] bg-[#2563eb] text-white" : "border-[#e2e8f0] bg-white text-[#0f1b2d] hover:border-[#94a3b8]"}`}
              >
                {p.name} · ${Math.round(price)}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-8 px-6 lg:px-8 max-w-7xl mx-auto">
        <ScrollRow ariaLabel={`${pkg.name} preview`} gap={20}>
          {items.map((it, i) => (
            <div key={i} className="flex flex-col items-start gap-2" data-testid={`wyg-doc-${pkg.id}-${i}`}>
              <PreviewCard item={it} size="full" />
              <div className="px-1 text-xs font-semibold text-[#0f1b2d]">{it.title}</div>
            </div>
          ))}
        </ScrollRow>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 mt-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <ul className="lg:col-span-2 space-y-2" data-testid={`wyg-list-${pkg.id}`}>
          {items.map((it, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-[#0f1b2d]">
              <span className="text-[#2563eb] mt-0.5">✓</span>
              <span><span className="font-semibold">{it.title}</span>{it.package_contents && <span className="text-[#475569]"> — {it.package_contents}</span>}</span>
            </li>
          ))}
        </ul>
        <a
          href={`/apply?package=${pkg.id}`}
          data-testid={`wyg-cta-${pkg.id}`}
          className="self-start inline-flex items-center justify-center gap-2 bg-[#2563eb] hover:bg-[#1d4ed8] text-white font-semibold px-6 py-3 rounded-md transition w-full lg:w-auto"
        >
          Get {pkg.name} — ${Math.round(pkg.price_normal)}
        </a>
      </div>
    </section>
  );
}

function FormArt() {
  return (
    <div className="w-full max-w-[200px] rounded-md border border-[#e2e8f0] bg-white p-3 shadow-sm">
      <div className="h-2 w-3/4 bg-[#0f1b2d] rounded mb-3" />
      <div className="space-y-1.5">
        <div className="h-1.5 w-1/4 bg-[#94a3b8] rounded" />
        <div className="h-5 w-full bg-[#f1f5f9] rounded border border-[#e2e8f0]" />
        <div className="h-1.5 w-1/3 bg-[#94a3b8] rounded mt-2" />
        <div className="h-5 w-full bg-[#f1f5f9] rounded border border-[#e2e8f0]" />
        <div className="grid grid-cols-2 gap-1.5 mt-2">
          <div className="h-1.5 w-3/4 bg-[#94a3b8] rounded" />
          <div className="h-1.5 w-3/4 bg-[#94a3b8] rounded" />
          <div className="h-5 bg-[#f1f5f9] rounded border border-[#e2e8f0]" />
          <div className="h-5 bg-[#f1f5f9] rounded border border-[#e2e8f0]" />
        </div>
      </div>
    </div>
  );
}

function PayArt() {
  return (
    <div className="w-full max-w-[200px] rounded-xl bg-gradient-to-br from-[#2563eb] to-[#1e40af] p-4 text-white shadow-md aspect-[1.6/1] flex flex-col justify-between">
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-bold tracking-widest uppercase opacity-90">Schengen Pay</div>
        <div className="w-7 h-5 rounded-sm bg-amber-400" />
      </div>
      <div className="font-mono text-sm tracking-[0.2em]">•••• •••• •••• 4242</div>
      <div className="flex items-center justify-between text-[10px]">
        <div>
          <div className="opacity-70">VALID</div>
          <div className="font-semibold">12/27</div>
        </div>
        <div className="flex gap-1">
          <div className="w-5 h-5 rounded-full bg-red-500/80" />
          <div className="w-5 h-5 rounded-full bg-amber-400/80 -ml-2" />
        </div>
      </div>
    </div>
  );
}

function FileArt() {
  return (
    <div className="w-full max-w-[200px] rounded-md border border-[#e2e8f0] bg-white shadow-sm overflow-hidden">
      <div className="px-3 py-2 bg-[#0f1b2d] text-white text-[10px] font-semibold">Your visa file</div>
      <div className="p-3 space-y-1.5">
        {["Visa Form", "Flight (PNR)", "Hotel Booking", "Cover Letter"].map((t) => (
          <div key={t} className="flex items-center justify-between text-[10px] py-1 px-2 rounded bg-[#f8fafc] border border-[#e2e8f0]">
            <span className="text-[#0f1b2d] font-medium truncate">{t}</span>
            <span className="text-[#2563eb] font-bold">↓</span>
          </div>
        ))}
      </div>
    </div>
  );
}

